import PropTypes from "prop-types";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { MailX, PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  FormInput,
  FormToggle,
  RemoveButton,
  SectionCard,
} from "components/BaseForm";
import CONSTANTS from "constants/Constants";
import { hasInvalidEmailDomain } from "helpers/contacts";
import { BLANK_EMAIL } from "routes/contact/form/defaults";
import {
  PrimaryToggle,
  useRemoveKeepingPrimary,
} from "routes/contact/form/sections/primaryRow";
import ReachabilityBadge from "routes/contact/form/sections/ReachabilityBadge";

const { MX_DOMAIN } = CONSTANTS.EMAIL_DISABLED_REASONS;

const domainOf = email => email?.split("@")[1] ?? "";

// Only disabled_reason says the domain check switched the row off; a row a user
// disabled by hand gets the neutral wording even though its domain is invalid.
const mxWarningFor = ({ email, disabled_reason }) =>
  disabled_reason === MX_DOMAIN
    ? `Disabled by domain check — ${domainOf(email)} has no MX records`
    : `Invalid domain — ${domainOf(email)} has no MX records`;

const MxWarning = ({ index }) => {
  const row = useWatch({ name: `email_addresses.${index}` });
  if (!hasInvalidEmailDomain(row)) return null;
  const message = mxWarningFor(row);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={message}
            className="tw:cursor-help tw:text-warning"
          />
        }
      >
        <MailX />
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
};

MxWarning.propTypes = { index: PropTypes.number.isRequired };

const EmailAddressesSection = () => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "email_addresses",
  });
  const removeRow = useRemoveKeepingPrimary("email_addresses", remove);
  const rows = useWatch({ control, name: "email_addresses" });
  const isEmailable = rows.some(row => row.enabled && row.subscribed);

  return (
    <SectionCard
      title={
        <span className="tw:flex tw:items-center tw:gap-2">
          Email Addresses
          <ReachabilityBadge label="Emailable" isReachable={isEmailable} />
        </span>
      }
    >
      {fields.length > 0 && (
        <Table className="tw:w-auto">
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-8" />
              <TableHead className="tw:w-80 tw:min-w-80">Email</TableHead>
              <TableHead className="tw:w-24">Primary</TableHead>
              <TableHead className="tw:w-24">Enabled</TableHead>
              <TableHead className="tw:w-24">Subscribed</TableHead>
              <TableHead className="tw:w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <MxWarning index={index} />
                </TableCell>
                <TableCell>
                  <FormInput
                    name={`email_addresses.${index}.email`}
                    type="email"
                    aria-label={`Email ${index + 1}`}
                    placeholder="name@example.com"
                  />
                </TableCell>
                <TableCell>
                  <PrimaryToggle
                    arrayName="email_addresses"
                    index={index}
                    label={`Email ${index + 1} primary`}
                  />
                </TableCell>
                <TableCell>
                  <FormToggle
                    name={`email_addresses.${index}.enabled`}
                    aria-label={`Email ${index + 1} enabled`}
                  />
                </TableCell>
                <TableCell>
                  <FormToggle
                    name={`email_addresses.${index}.subscribed`}
                    aria-label={`Email ${index + 1} subscribed`}
                  />
                </TableCell>
                <TableCell>
                  <RemoveButton
                    label={`Remove email ${index + 1}`}
                    onClick={() => removeRow(index)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tw:self-start"
        onClick={() => append({ ...BLANK_EMAIL })}
      >
        <PlusIcon data-icon="inline-start" /> Add Email
      </Button>
    </SectionCard>
  );
};

export default EmailAddressesSection;
